import { ref, watch, onUnmounted } from 'vue';

// 定制: 新会话(或现有会话收到新消息)刚进入列表时, 整行闪几下引导客服注意。
// 判定信号 = 会话 last_activity_at 距当前时间在 NEW_WINDOW_MS 内(新消息会刷新该时间戳)。
// 每个会话 id 一次会话只闪一次(模块级 flashedIds 去重), 避免轮询/翻页重复闪。

const flashedIds = new Set();
// last_activity_at 距今在这个窗口内才算"刚进来"(允许服务端时钟略超前 → 用负数也放行)
const NEW_WINDOW_MS = 15_000;
// 闪烁类保留时长, 略大于 CSS 动画 2.4s, 动画播完自然停
const FLASH_HOLD_MS = 3000;

/**
 * @param {() => object} getChat 返回当前会话对象的 getter(读 props.source)
 * @returns {{ flashNew: import('vue').Ref<boolean> }}
 */
export function useNewConversationFlash(getChat) {
  const flashNew = ref(false);
  let timer = null;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const evaluate = () => {
    const chat = getChat();
    clearTimer();
    const id = chat?.id;
    if (!id) {
      flashNew.value = false;
      return;
    }
    // 本会话已闪过 → 不再闪
    if (flashedIds.has(id)) {
      flashNew.value = false;
      return;
    }
    const ts = Number(chat.last_activity_at);
    const ageMs = Number.isFinite(ts) ? Date.now() - ts * 1000 : Infinity;
    if (ageMs < NEW_WINDOW_MS) {
      // 刚进来 → 闪, 并登记避免重复
      flashedIds.add(id);
      flashNew.value = true;
      timer = setTimeout(() => {
        flashNew.value = false;
      }, FLASH_HOLD_MS);
    } else {
      flashNew.value = false;
    }
  };

  // 监听 id(行回收)和 last_activity_at(收到新消息)变化
  watch(
    () => [getChat()?.id, getChat()?.last_activity_at],
    evaluate,
    { immediate: true }
  );

  onUnmounted(clearTimer);

  return { flashNew };
}
